// The app router, and every default it is created with.
//
// Extracted from main.tsx so the options are a unit — `router.test.ts` asserts
// them on a router built by this function, which is the only way a defaults
// regression fails a build (nothing in a screen test can see what main.tsx
// passed to `createRouter`).
//
// WHY THESE DEFAULTS (measured 2026-09-13): every section switch felt slow.
// `POST /campaigns/overview` reads 2.07 M rows per request — three aggregates
// over `reporting_campaign_daily_provider_v1`, whose inner view materialises
// both reporting fact tables — for 2.3–6 s of server time, and D1 runs one
// database's statements serially, so every concurrent request queues behind it
// (a 59 ms `/me` took 2.9 s). Route loaders `warm()` before the route renders,
// so for that whole wait a click produced nothing: the old screen stayed put,
// the sidebar item did not move, and the user clicked again. The SQL is being
// rewritten separately; this file makes the wait visible and stops the
// hover-preload from making it longer.

import type { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { RouteError } from '@/components/route-error';
import { RoutePending } from '@/components/route-pending';

export { PENDING_MIN_MS, PENDING_MS, PRELOAD_DELAY_MS } from '@/lib/navigation-timing';
import { PENDING_MIN_MS, PENDING_MS, PRELOAD_DELAY_MS } from '@/lib/navigation-timing';

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadDelay: PRELOAD_DELAY_MS,
    // The TanStack Query cache is the single authority on freshness. Router's own
    // `defaultPreloadStaleTime` defaults to 30s, which would put a second, shorter
    // cache in front of it — so a loader would be skipped as "fresh" by the router
    // while the query behind it was already stale, and the two would disagree
    // about what needs refetching. Zero delegates the whole question downward.
    defaultPreloadStaleTime: 0,
    // Every route's loader failure lands here. Without it the router renders its
    // own bare "Something went wrong!" with no retry — which is what a rejected
    // `warm()` produced on every one of the 22 routes that now have a loader,
    // making all 56 in-screen retry affordances unreachable on a cold load.
    defaultErrorComponent: RouteError,
    // What a route shows while its loader is still running. Without it the
    // router keeps the previous match rendered until the new one resolves, so
    // a slow loader looks like a click that did nothing. A `beforeLoad` counts
    // as running too: the pending timer is armed before it is even invoked, so
    // a redirect route (`/`, routes/index.tsx — session plus a 1.5 s copilot
    // probe) shows the skeleton like any loader once its redirect lands past
    // `defaultPendingMs`; a redirect that lands within it shows nothing.
    defaultPendingComponent: RoutePending,
    // Only a section switch gets the skeleton. A route whose `loaderDeps`
    // derives from `search` mints a new match on every filter, sort, range or
    // tab change, and the skeleton would replace the whole screen — including
    // the control just used — 150 ms into a cache miss. Those routes set
    // `pendingMs: Infinity` (guarded by `routes/-route-pending.guard.test.ts`);
    // the progress bar still runs there, because router status is pending
    // regardless. The cost: a section switch ONTO one of them shows the bar and
    // the outgoing screen, not the skeleton.
    defaultPendingMs: PENDING_MS,
    defaultPendingMinMs: PENDING_MIN_MS,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;
